import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createPortal} from 'react-dom';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {selectTextAreaOpen} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {TextEditorTooltip} from './TextEditorTooltip';
import './TextEditorVariant.sass';

const letterCasingToTextTransform = {
  upper: 'uppercase',
  lower: 'lowercase',
  capitalize: 'capitalize',
};

export const TextEditorVariant = () => {
  const {canvas, activeObjects} = useContext(EventCreateContext);
  const textAreaRef = useRef(null);
  const [text, setText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [fontFamily, setFontFamily] = useState('');
  const [fontWeight, setFontWeight] = useState('');
  const [textAlign, setTextAlign] = useState('');
  const [letterCasing, setLetterCasing] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(true);
  const [heightDifference, setHeightDifference] = useState(0);
  const textAreaOpen = useSelector(selectTextAreaOpen);
  const dispatch = useDispatch();
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);

  const getTextTransform = () => letterCasingToTextTransform[letterCasing] || 'none';

  useEffect(() => {
    if (textAreaOpen && activeObjects?.length > 0) {
      const [activeObject] = activeObjects;
      setFontFamily(activeObject?.fontFamily);
      setFontWeight(activeObject?.fontWeight);
      setTextAlign(activeObject?.textAlign);
      setLetterCasing(activeObject?.letterCasing);
      setText(activeObject?.text);
      setOriginalText(activeObject?.text);
    }
  }, [canvas, textAreaOpen]);

  useEffect(() => {
    const onTextEditingEntered = () =>
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {textAreaOpen: true},
      });

    canvas?.on({
      'text:editing:entered': onTextEditingEntered,
    });

    return () => {
      canvas?.off({
        'text:editing:entered': onTextEditingEntered,
      });
    };
  }, [canvas]);

  useEffect(() => {
    const detectKeyboard = () => {
      if (window.visualViewport) {
        const viewportHandler = () => {
          setHeightDifference(window.innerHeight - window.visualViewport.height);
        };

        window.visualViewport.addEventListener('resize', viewportHandler);
        return () => window.visualViewport.removeEventListener('resize', viewportHandler);
      }

      return () => {
        setHeightDifference(0);
      };
    };

    return detectKeyboard();
  }, []);

  useEffect(() => {
    const hiddenTextarea = document.querySelector('textarea[data-fabric-hiddentextarea]');

    if (textAreaOpen && hiddenTextarea && !isDesktop) {
      hiddenTextarea.style.display = 'none';
    }

    return () => {
      if (hiddenTextarea) {
        hiddenTextarea.style.display = '';
      }
    };
  }, [textAreaOpen, isDesktop]);

  const changeText = (newText) => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      setText(newText);
      activeObject.text = newText;
    }
  };

  const onTextChangeHandler = useCallback(({target: {value}}) => changeText(value), [canvas]);

  const closeEditor = () => {
    const activeObject = canvas?.getActiveObject();
    activeObject?.exitEditing?.();
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {textAreaOpen: false},
    });
    canvas.requestRenderAll();
  };

  const onCancelClickHandler = () => {
    changeText(originalText);
    closeEditor();
  };

  const onClearClickHandler = () => {
    changeText('');
  };

  const onDoneClickHandler = () => {
    changeText(text);
    closeEditor();
  };

  if (isDesktop || !textAreaOpen) {
    return null;
  }

  return createPortal(
    <div className="text-editor-variant">
      <div className="text-editor-variant__header">
        <Button
          data-qa-id="step1-texteditor-close"
          className="text-editor-variant__header__button text-editor-variant__button-cancel"
          variant="featured"
          size="large"
          onClick={onCancelClickHandler}
        >
          <Typography variant="list1">Cancel</Typography>
        </Button>
        <Button
          data-qa-id="step1-texteditor-close"
          variant="transparent"
          size="large"
          onClick={onClearClickHandler}
        >
          <Typography variant="list1">Clear</Typography>
        </Button>
        <Button
          data-qa-id="step1-texteditor-close"
          className="text-editor-variant__header__button text-editor-variant__button-done"
          variant="featured"
          size="large"
          onClick={onDoneClickHandler}
        >
          <Typography variant="list1">Done</Typography>
        </Button>
      </div>
      <div className="text-editor-variant__body">
        <div className="text-editor-variant__body__content">
          <textarea
            ref={textAreaRef}
            style={{
              fontFamily,
              fontWeight,
              textAlign,
              textTransform: getTextTransform(),
            }}
            onChange={onTextChangeHandler}
            value={text}
            key={originalText}
            data-qa-id="step1-texteditor-input"
            placeholder="Enter your text here..."
            className="text-editor-variant__body__content__textarea"
          />
        </div>
      </div>
      <div className="text-editor-variant__tooltip" style={{bottom: `${heightDifference}px`}}>
        <TextEditorTooltip
          isVisible={isTooltipVisible}
          onClose={() => setIsTooltipVisible(false)}
        />
      </div>
    </div>,
    document.getElementById('pbModalRoot')
  );
};
